import React, { useState } from 'react';
import { css } from '@emotion/css';
import { TextField } from '@design-system/TextField';
import { Button } from '@design-system/Button';
import { Spacing } from '@components/Spacing';
import { useLoading } from '@hooks/useLoading';
import { ErrorMessage } from '@components/ErrorMessage';
import { useErrorHandling } from '@hooks/useErrorHandling';
import { isValidEmail } from '@utils/validation';
import { updateUserCredits } from '@remotes/updateUserCredits';
import { updateStrokeRecordingPermission } from '@remotes/updateStrokeRecordingPermission';
import { broadcastNotification, BroadcastNotificationResponse } from '@remotes/broadcastNotification';
import { fetchUserSubscription } from '@remotes/fetchUserSubscription';
import { CurrentSubscriptionResponse } from '@remotes/fetchCurrentSubscription';
import { showSuccessMsg } from '@utils/notification';
import StrokeDataViewer from '@containers/StrokeDataViewer';

type AdminTab = 'credits' | 'stroke-permission' | 'broadcast' | 'stroke-data';

const AdminDashboard: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<AdminTab>('credits');

  // Credit update state
  const [userEmail, setUserEmail] = useState('');
  const [username, setUsername] = useState('');
  const [imageCredits, setImageCredits] = useState('');
  const [styleCredits, setStyleCredits] = useState('');
  const [creditsLoading, startCreditsLoading, stopCreditsLoading] = useLoading();
  const [creditsError, handleCreditsError, clearCreditsError, setCreditsError] = useErrorHandling();

  // Stroke recording permission state
  const [strokeUserEmail, setStrokeUserEmail] = useState('');
  const [strokeUsername, setStrokeUsername] = useState('');
  const [allowStrokeRecording, setAllowStrokeRecording] = useState<boolean>(true);
  const [strokeLoading, startStrokeLoading, stopStrokeLoading] = useLoading();
  const [strokeError, handleStrokeError, clearStrokeError, setStrokeError] = useErrorHandling();

  // Broadcast notification state
  const [notificationMessage, setNotificationMessage] = useState('');
  const [batchSize, setBatchSize] = useState('');
  const [isDryRun, setIsDryRun] = useState(true);
  const [broadcastLoading, startBroadcastLoading, stopBroadcastLoading] = useLoading();
  const [broadcastError, handleBroadcastError, clearBroadcastError, setBroadcastError] = useErrorHandling();
  const [broadcastResult, setBroadcastResult] = useState<BroadcastNotificationResponse | null>(null);

  // View subscription state
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [subscriptionResult, setSubscriptionResult] = useState<CurrentSubscriptionResponse | null>(null);
  const [subscriptionLoading, startSubscriptionLoading, stopSubscriptionLoading] = useLoading();
  const [subscriptionError, handleSubscriptionError, clearSubscriptionError, setSubscriptionError] = useErrorHandling();

  // Credit update handlers
  const handleUserEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value);
    setUsername(''); // Clear username when email is entered
    clearCreditsError();
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setUserEmail(''); // Clear email when username is entered
    clearCreditsError();
  };

  const handleImageCreditsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setImageCredits(value);
      clearCreditsError();
    }
  };

  const handleStyleCreditsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setStyleCredits(value);
      clearCreditsError();
    }
  };

  // Stroke recording permission handlers
  const handleStrokeUserEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStrokeUserEmail(e.target.value);
    setStrokeUsername(''); // Clear username when email is entered
    clearStrokeError();
  };

  const handleStrokeUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStrokeUsername(e.target.value);
    setStrokeUserEmail(''); // Clear email when username is entered
    clearStrokeError();
  };

  // Broadcast notification handlers
  const handleNotificationMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Limit to 500 characters
    if (value.length <= 500) {
      setNotificationMessage(value);
    }
    clearBroadcastError();
    setBroadcastResult(null);
  };

  const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 5000)) {
      setBatchSize(value);
      clearBroadcastError();
    }
  };

  // View subscription handlers
  const handleSubscriptionSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubscriptionSearch(e.target.value);
    clearSubscriptionError();
    setSubscriptionResult(null);
  };

  const handleViewSubscription = async () => {
    const trimmedSearch = subscriptionSearch.trim();

    if (!trimmedSearch) {
      return setSubscriptionError('Please enter a username or email address.');
    }

    // Determine if input is email or username
    const isEmail = isValidEmail(trimmedSearch);

    try {
      startSubscriptionLoading();
      const result = await fetchUserSubscription({
        email: isEmail ? trimmedSearch : undefined,
        username: !isEmail ? trimmedSearch : undefined,
      });
      setSubscriptionResult(result);
      clearSubscriptionError();
    } catch (e) {
      handleSubscriptionError(e);
      setSubscriptionResult(null);
    } finally {
      stopSubscriptionLoading();
    }
  };

  const handleSubscriptionSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleViewSubscription();
    }
  };

  const handleCreditsSubmit = async () => {
    // Validation
    const imageCreditsNum = imageCredits ? parseInt(imageCredits) : undefined;
    const styleCreditsNum = styleCredits ? parseInt(styleCredits) : undefined;

    if (imageCreditsNum === undefined && styleCreditsNum === undefined) {
      return setCreditsError('Please enter at least one credit amount.');
    }

    if ((imageCreditsNum !== undefined && imageCreditsNum < 0) || 
        (styleCreditsNum !== undefined && styleCreditsNum < 0)) {
      return setCreditsError('Credit amounts cannot be negative.');
    }

    // Validate email or username if provided
    const trimmedEmail = userEmail.trim();
    const trimmedUsername = username.trim();

    if (trimmedEmail && trimmedUsername) {
      return setCreditsError('Please provide either email or username, not both.');
    }

    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      return setCreditsError('Please enter a valid email address.');
    }

    try {
      startCreditsLoading();
      await updateUserCredits({
        email: trimmedEmail || undefined,
        username: trimmedUsername || undefined,
        imageCredits: imageCreditsNum,
        styleCredits: styleCreditsNum,
      });

      let targetUser = 'your account';
      if (trimmedEmail) {
        targetUser = `user ${trimmedEmail}`;
      } else if (trimmedUsername) {
        targetUser = `user ${trimmedUsername}`;
      }

      const creditsMessage = [];
      if (imageCreditsNum !== undefined) {
        creditsMessage.push(`${imageCreditsNum} image credits`);
      }
      if (styleCreditsNum !== undefined) {
        creditsMessage.push(`${styleCreditsNum} style credits`);
      }

      showSuccessMsg(
        `Successfully updated ${targetUser} with ${creditsMessage.join(' and ')}`
      );
      
      // Clear form
      setUserEmail('');
      setUsername('');
      setImageCredits('');
      setStyleCredits('');
    } catch (e) {
      handleCreditsError(e);
    } finally {
      stopCreditsLoading();
    }
  };

  const handleStrokePermissionSubmit = async () => {
    // Validate email or username if provided
    const trimmedEmail = strokeUserEmail.trim();
    const trimmedUsername = strokeUsername.trim();

    if (!trimmedEmail && !trimmedUsername) {
      return setStrokeError('Please provide either email or username.');
    }

    if (trimmedEmail && trimmedUsername) {
      return setStrokeError('Please provide either email or username, not both.');
    }

    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      return setStrokeError('Please enter a valid email address.');
    }

    try {
      startStrokeLoading();
      await updateStrokeRecordingPermission({
        email: trimmedEmail || undefined,
        username: trimmedUsername || undefined,
        allow_stroke_data_recording: allowStrokeRecording,
      });

      let targetUser = trimmedEmail || trimmedUsername;
      const permissionStatus = allowStrokeRecording ? 'enabled' : 'disabled';

      showSuccessMsg(
        `Successfully ${permissionStatus} stroke data recording for user ${targetUser}`
      );
      
      // Clear form
      setStrokeUserEmail('');
      setStrokeUsername('');
    } catch (e) {
      handleStrokeError(e);
    } finally {
      stopStrokeLoading();
    }
  };

  const handleBroadcastSubmit = async () => {
    if (!notificationMessage.trim()) {
      return setBroadcastError('Please enter a notification message.');
    }

    if (notificationMessage.length > 500) {
      return setBroadcastError('Message must be 500 characters or less.');
    }

    const batchSizeNum = batchSize ? parseInt(batchSize) : undefined;

    try {
      startBroadcastLoading();
      const result = await broadcastNotification({
        message: notificationMessage.trim(),
        batch_size: batchSizeNum,
        dry_run: isDryRun,
      });

      setBroadcastResult(result);

      if (result.success) {
        if ('dry_run' in result && result.dry_run) {
          showSuccessMsg('Dry run completed successfully - no notifications were sent');
        } else {
          showSuccessMsg('Broadcast notification completed successfully');
          // Clear form after successful broadcast (not dry run)
          if (!isDryRun) {
            setNotificationMessage('');
            setBatchSize('');
          }
        }
      }
    } catch (e) {
      handleBroadcastError(e);
    } finally {
      stopBroadcastLoading();
    }
  };

  const isCreditsSubmitDisabled = 
    (!imageCredits && !styleCredits) || 
    creditsLoading ||
    creditsError != null;

  const isStrokeSubmitDisabled = 
    (!strokeUserEmail.trim() && !strokeUsername.trim()) || 
    strokeLoading ||
    strokeError != null;

  const isBroadcastSubmitDisabled = 
    !notificationMessage.trim() || 
    notificationMessage.length > 500 ||
    broadcastLoading ||
    broadcastError != null;

  const isViewSubscriptionDisabled = 
    !subscriptionSearch.trim() || 
    subscriptionLoading ||
    subscriptionError != null;

  const renderBroadcastResult = () => {
    if (!broadcastResult) return null;

    if (!broadcastResult.success) {
      return (
        <div className={resultStyle}>
          <h3 className={resultTitleStyle}>Broadcast Failed</h3>
          <p className={errorTextStyle}>{broadcastResult.error}</p>
          <p>Total devices: {broadcastResult.total_devices}</p>
        </div>
      );
    }

    if ('dry_run' in broadcastResult && broadcastResult.dry_run) {
      const { preview } = broadcastResult;
      return (
        <div className={resultStyle}>
          <h3 className={resultTitleStyle}>Dry Run Preview</h3>
          <div className={previewStyle}>
            <p><strong>Message:</strong> "{preview.notification_message}"</p>
            <p><strong>Total devices:</strong> {preview.total_devices}</p>
            <p><strong>Batch size:</strong> {preview.batch_size}</p>
            <p><strong>Total batches:</strong> {preview.total_batches}</p>
          </div>
        </div>
      );
    }

    // Type guard to ensure we have a response with summary
    if (!('summary' in broadcastResult)) {
      return null;
    }

    const { summary } = broadcastResult;
    const hasErrors = 'errors' in broadcastResult;

    return (
      <div className={resultStyle}>
        <h3 className={resultTitleStyle}>
          Broadcast {hasErrors ? 'Completed with Errors' : 'Completed Successfully'}
        </h3>
        <div className={summaryStyle}>
          <p><strong>Total devices:</strong> {summary.total_devices}</p>
          <p><strong>Total batches:</strong> {summary.total_batches}</p>
          <p><strong>Successful notifications:</strong> {summary.successful_notifications}</p>
          <p><strong>Failed notifications:</strong> {summary.failed_notifications}</p>
          <p><strong>Success rate:</strong> {summary.success_rate.toFixed(1)}%</p>
        </div>
        
        {hasErrors && 'errors' in broadcastResult && (
          <div className={errorsStyle}>
            <h4>Error Details:</h4>
            <p><strong>Failed batches:</strong> {broadcastResult.errors.failed_batches}</p>
            {broadcastResult.errors.batch_details.map((batch, index) => (
              <div key={index} className={batchErrorStyle}>
                <p><strong>Batch {batch.batch}:</strong> {batch.device_count} devices - {batch.error}</p>
              </div>
            ))}
            {broadcastResult.errors.additional_failures && (
              <p><em>+ {broadcastResult.errors.additional_failures} additional failed batches</em></p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSubscriptionDetails = () => {
    if (!subscriptionResult) return null;

    const statusColors: Record<string, string> = {
      ACTIVE: '#4caf50',
      EXPIRED: '#ff6b6b',
      CANCELLED: '#ff9800',
    };

    const statusColor = statusColors[subscriptionResult.status] || '#aaaaaa';

    return (
      <div className={subscriptionCardStyle}>
        <h3 className={subscriptionCardTitleStyle}>Subscription Details</h3>
        <div className={subscriptionDetailsStyle}>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Status:</span>
            <span className={subscriptionValueStyle} style={{ color: statusColor }}>
              {subscriptionResult.status}
            </span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Plan:</span>
            <span className={subscriptionValueStyle}>{subscriptionResult.product.display_name}</span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Tier:</span>
            <span className={subscriptionValueStyle}>{subscriptionResult.product.tier}</span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Image Credits:</span>
            <span className={subscriptionValueStyle}>{subscriptionResult.image_credits}</span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Style Credits:</span>
            <span className={subscriptionValueStyle}>{subscriptionResult.style_credits}</span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Period Type:</span>
            <span className={subscriptionValueStyle}>{subscriptionResult.period_type}</span>
          </div>
          <div className={subscriptionDetailRowStyle}>
            <span className={subscriptionLabelStyle}>Start Date:</span>
            <span className={subscriptionValueStyle}>
              {new Date(subscriptionResult.start_date).toLocaleDateString()}
            </span>
          </div>
          {subscriptionResult.end_date && (
            <div className={subscriptionDetailRowStyle}>
              <span className={subscriptionLabelStyle}>End Date:</span>
              <span className={subscriptionValueStyle}>
                {new Date(subscriptionResult.end_date).toLocaleDateString()}
              </span>
            </div>
          )}
          {subscriptionResult.payment_source && (
            <div className={subscriptionDetailRowStyle}>
              <span className={subscriptionLabelStyle}>Payment Source:</span>
              <span className={subscriptionValueStyle}>{subscriptionResult.payment_source}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCreditsTab = () => (
    <>
      {/* View User Subscription Section */}
      <div className={sectionStyle}>
        <h2 className={sectionTitleStyle}>View User Subscription</h2>
        <p className={subtitleStyle}>Search for a user's current subscription by username or email</p>

        <Spacing size={30} />

        <div className={formStyle}>
          <TextField
            placeholder="Enter username or email"
            type="text"
            value={subscriptionSearch}
            onChange={handleSubscriptionSearchChange}
            onKeyDown={handleSubscriptionSearchKeyDown}
          />

          <Spacing size={20} />

          {subscriptionError && (
            <>
              <ErrorMessage text={subscriptionError} />
              <Spacing size={20} />
            </>
          )}

          <Button
            onClick={handleViewSubscription}
            disabled={isViewSubscriptionDisabled}
            loading={subscriptionLoading}
          >
            Search Subscription
          </Button>

          <Spacing size={20} />

          {renderSubscriptionDetails()}
        </div>
      </div>

      <Spacing size={60} />

      {/* Update User Credits Section */}
      <div className={sectionStyle}>
        <h2 className={sectionTitleStyle}>Update User Credits</h2>
        <p className={subtitleStyle}>Update user credit balances</p>

        <Spacing size={30} />

        <div className={formStyle}>
          <TextField
            placeholder="User email address (leave empty to update your own account)"
            type="email"
            value={userEmail}
            onChange={handleUserEmailChange}
          />

          <Spacing size={20} />

          <TextField
            placeholder="OR username (alternative to email)"
            type="text"
            value={username}
            onChange={handleUsernameChange}
          />

          <Spacing size={20} />

          <TextField
            placeholder="New image credits value (leave empty to keep unchanged)"
            type="text"
            value={imageCredits}
            onChange={handleImageCreditsChange}
          />

          <Spacing size={20} />

          <TextField
            placeholder="New style credits value (leave empty to keep unchanged)"
            type="text"
            value={styleCredits}
            onChange={handleStyleCreditsChange}
          />

          <Spacing size={30} />

          <div className={noteStyle}>
            <p><strong>Note:</strong> Credit values will be set to the specified amounts (not added to existing values).</p>
            <p>Leave both email and username empty to update your own account. Provide either email OR username, not both. At least one credit type must be specified.</p>
          </div>

          <Spacing size={20} />

          {creditsError && (
            <>
              <ErrorMessage text={creditsError} />
              <Spacing size={20} />
            </>
          )}

          <Button
            onClick={handleCreditsSubmit}
            disabled={isCreditsSubmitDisabled}
            loading={creditsLoading}
          >
            Update Credits
          </Button>
        </div>
      </div>
    </>
  );

  const renderBroadcastTab = () => (
    <div className={sectionStyle}>
      <h2 className={sectionTitleStyle}>Broadcast Notification</h2>
      <p className={subtitleStyle}>Send push notifications to all users</p>

      <Spacing size={30} />

      <div className={formStyle}>
        <TextField
          placeholder="Notification message (max 500 characters)"
          type="text"
          value={notificationMessage}
          onChange={handleNotificationMessageChange}
        />
        <div className={characterCountStyle}>
          {notificationMessage.length}/500 characters
        </div>

        <Spacing size={20} />

        <TextField
          placeholder="Batch size (1-5000, default: 1000)"
          type="text"
          value={batchSize}
          onChange={handleBatchSizeChange}
        />

        <Spacing size={20} />

        <div className={checkboxContainerStyle}>
          <label className={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={isDryRun}
              onChange={(e) => {
                setIsDryRun(e.target.checked);
                clearBroadcastError();
                setBroadcastResult(null);
              }}
              className={checkboxStyle}
            />
            <span>Dry run (preview only - no notifications sent)</span>
          </label>
        </div>

        <Spacing size={30} />

        <div className={noteStyle}>
          <p><strong>Warning:</strong> This will send notifications to ALL users with the app installed.</p>
          <p>Use dry run first to preview the broadcast before sending actual notifications.</p>
          <p>Batch size controls how many devices are notified at once (1-5000, default: 1000).</p>
        </div>

        <Spacing size={20} />

        {broadcastError && (
          <>
            <ErrorMessage text={broadcastError} />
            <Spacing size={20} />
          </>
        )}

        {renderBroadcastResult()}

        {broadcastResult && <Spacing size={20} />}

        <Button
          onClick={handleBroadcastSubmit}
          disabled={isBroadcastSubmitDisabled}
          loading={broadcastLoading}
        >
          {isDryRun ? 'Preview Broadcast' : 'Send Notification'}
        </Button>
      </div>
    </div>
  );

  const renderStrokePermissionTab = () => (
    <div className={sectionStyle}>
      <h2 className={sectionTitleStyle}>Update Stroke Recording Permission</h2>
      <p className={subtitleStyle}>Enable or disable stroke data recording for users</p>

      <Spacing size={30} />

      <div className={formStyle}>
        <TextField
          placeholder="User email address"
          type="email"
          value={strokeUserEmail}
          onChange={handleStrokeUserEmailChange}
        />

        <Spacing size={20} />

        <TextField
          placeholder="OR username (alternative to email)"
          type="text"
          value={strokeUsername}
          onChange={handleStrokeUsernameChange}
        />

        <Spacing size={30} />

        <div className={checkboxContainerStyle}>
          <label className={checkboxLabelStyle}>
            <input
              type="radio"
              name="strokePermission"
              checked={allowStrokeRecording}
              onChange={() => {
                setAllowStrokeRecording(true);
                clearStrokeError();
              }}
              className={checkboxStyle}
            />
            <span>Allow stroke data recording</span>
          </label>
        </div>

        <Spacing size={10} />

        <div className={checkboxContainerStyle}>
          <label className={checkboxLabelStyle}>
            <input
              type="radio"
              name="strokePermission"
              checked={!allowStrokeRecording}
              onChange={() => {
                setAllowStrokeRecording(false);
                clearStrokeError();
              }}
              className={checkboxStyle}
            />
            <span>Disable stroke data recording</span>
          </label>
        </div>

        <Spacing size={30} />

        <div className={noteStyle}>
          <p><strong>Note:</strong> This controls whether the user can record stroke data for AI training purposes.</p>
          <p>Provide either email OR username, not both. Both fields are required to identify the user.</p>
        </div>

        <Spacing size={20} />

        {strokeError && (
          <>
            <ErrorMessage text={strokeError} />
            <Spacing size={20} />
          </>
        )}

        <Button
          onClick={handleStrokePermissionSubmit}
          disabled={isStrokeSubmitDisabled}
          loading={strokeLoading}
        >
          Update Permission
        </Button>
      </div>
    </div>
  );

  return (
    <div className={containerStyle}>
      <div className={wrapperStyle}>
        <h1 className={titleStyle}>Admin Dashboard</h1>
        
        <Spacing size={30} />

        {/* Tab Navigation */}
        <div className={tabContainerStyle}>
          <button
            onClick={() => setActiveTab('credits')}
            className={activeTab === 'credits' ? activeTabStyle : inactiveTabStyle}
          >
            Credits
          </button>
          <button
            onClick={() => setActiveTab('stroke-permission')}
            className={activeTab === 'stroke-permission' ? activeTabStyle : inactiveTabStyle}
          >
            Stroke Permission
          </button>
          <button
            onClick={() => setActiveTab('stroke-data')}
            className={activeTab === 'stroke-data' ? activeTabStyle : inactiveTabStyle}
          >
            Stroke Data
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            className={activeTab === 'broadcast' ? activeTabStyle : inactiveTabStyle}
          >
            Broadcast
          </button>
        </div>

        <Spacing size={40} />

        {/* Tab Content */}
        {activeTab === 'credits' && renderCreditsTab()}
        {activeTab === 'stroke-permission' && renderStrokePermissionTab()}
        {activeTab === 'stroke-data' && <StrokeDataViewer />}
        {activeTab === 'broadcast' && renderBroadcastTab()}
      </div>
    </div>
  );
};



const containerStyle = css`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 40px 20px;
`;

const wrapperStyle = css`
  max-width: 600px;
  width: 100%;
`;

const titleStyle = css`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin: 0;
`;

const sectionStyle = css`
  width: 100%;
`;

const sectionTitleStyle = css`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  margin: 0;
`;

const subtitleStyle = css`
  font-size: 16px;
  font-weight: 400;
  color: #aaaaaa;
  text-align: center;
  margin: 8px 0 0 0;
`;

const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const noteStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;

  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    line-height: 1.4;
    color: #aaaaaa;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #ffffff;
    }
  }
`;

const characterCountStyle = css`
  align-self: flex-end;
  font-size: 12px;
  color: #aaaaaa;
  margin-top: 4px;
`;

const checkboxContainerStyle = css`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const checkboxLabelStyle = css`
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  
  span {
    margin-left: 8px;
  }
`;

const checkboxStyle = css`
  width: 16px;
  height: 16px;
  accent-color: #007bff;
`;

const resultStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  margin-top: 20px;
`;

const resultTitleStyle = css`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const previewStyle = css`
  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #aaaaaa;
    
    strong {
      color: #ffffff;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const summaryStyle = css`
  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #aaaaaa;
    
    strong {
      color: #ffffff;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const errorsStyle = css`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #ff6b6b;
    margin: 0 0 12px 0;
  }
  
  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #aaaaaa;
    
    strong {
      color: #ffffff;
    }
    
    em {
      color: #ff6b6b;
    }
  }
`;

const batchErrorStyle = css`
  background: rgba(255, 107, 107, 0.1);
  border-left: 3px solid #ff6b6b;
  padding: 8px 12px;
  margin: 8px 0;
  border-radius: 4px;
  
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const errorTextStyle = css`
  color: #ff6b6b;
  font-weight: 500;
`;

const tabContainerStyle = css`
  display: flex;
  justify-content: center;
  gap: 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const activeTabStyle = css`
  background: #007bff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 160px;

  &:hover {
    background: #0056b3;
  }
`;

const inactiveTabStyle = css`
  background: transparent;
  color: #aaaaaa;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 160px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
  }
`;

const subscriptionCardStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  margin-top: 20px;
`;

const subscriptionCardTitleStyle = css`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const subscriptionDetailsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const subscriptionDetailRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const subscriptionLabelStyle = css`
  font-size: 14px;
  font-weight: 500;
  color: #aaaaaa;
`;

const subscriptionValueStyle = css`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

export default AdminDashboard;